import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const SiteContext = createContext({ site: null, content: null, refresh: () => {}, refreshContent: () => {} });

function hexToHsl(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const lig = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lig > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return [Math.round(hue * 360), Math.round(sat * 100), Math.round(lig * 100)];
}

function applyTheme(s) {
  if (!s) return;
  document.title = s.page_title || s.site_name || "justlaundry.co.uk";
  const root = document.documentElement;
  try {
    if (s.primary_color) {
      const [h, sat, l] = hexToHsl(s.primary_color);
      root.style.setProperty("--brand", `${h} ${sat}% ${l}%`);
      root.style.setProperty("--brand-hover", `${h} ${sat}% ${Math.max(0, l - 8)}%`);
      root.style.setProperty("--primary", `${h} ${sat}% ${l}%`);
      root.style.setProperty("--ring", `${h} ${sat}% ${l}%`);
      root.style.setProperty("--accent", `${h} ${Math.min(96, sat)}% 93%`);
      root.style.setProperty("--accent-foreground", `${h} ${sat}% 38%`);
    }
    if (s.secondary_color) {
      const [h, sat, l] = hexToHsl(s.secondary_color);
      root.style.setProperty("--brand-orange", `${h} ${sat}% ${l}%`);
      root.style.setProperty("--brand-orange-hover", `${h} ${sat}% ${Math.max(0, l - 6)}%`);
    }
  } catch (e) {
    /* ignore malformed color */
  }
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const [content, setContent] = useState(null);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSite(data.site);
    applyTheme(data.site);
    return data.site;
  }, []);

  const refreshContent = useCallback(async () => {
    const { data } = await api.get("/content/landing");
    setContent(data);
    return data;
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
    refreshContent().catch(() => {});
  }, [refresh, refreshContent]);

  return <SiteContext.Provider value={{ site, content, refresh, refreshContent }}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
